import { type UploadResult } from 'client/widget-components/components/FileUpload/types';

export enum CustomizationType {
    TEXT = 'TEXT',
    CHECKBOX = 'CHECKBOX',
    FILE = 'FILE',
}

export type CustomizationSnipcartPayloadFile = Omit<
    UploadResult,
    'uploadedAt'
> & {
    uploaded_at: Date;
};

export type ProductCustomization = {
    id: string;
    value:
        | string[]
        | string
        | CustomizationSnipcartPayloadFile[]
        | null
        | undefined;
    type: CustomizationType;
};

export interface AddProductPayload {
    product_id: string;
    variation_id: string;
    quantity?: number;
    metadata?: Record<string, unknown>;
    product_customizations?: ProductCustomization[];
}

export interface BuyProductPayload {
    product_id: string;
    variation_id: string;
    /** Optional, provide only when buying subscription */
    plan_id?: string;
    /** Optional, defaults to 1 (subscription only support 1) */
    quantity?: number;
    metadata?: Record<string, unknown>;
    product_customizations?: ProductCustomization[];
}

export interface BuyBookingPayload {
    /** Display name of the event */
    event_name: string;
    /** Event type that can be booked */
    event_type_id: number;
    /** Start of the booking slot to reserve example */
    slot_start: string;
    /** (Optional) Duration in minutes of the slot to reserve - NOTE: Only for event types with variable length */
    slot_duration?: number;
    /** Selected by user from Booker atom, example: 'America/Montreal' */
    time_zone: string;
    /** Name of the customer, used to prefill checkout */
    customer_name?: string;
    /** Email of the customer, used to prefill checkout */
    customer_email?: string;
    /** JSON string of metadata */
    metadata: object;
    /** Maps to Cal.com bookingFields, example: { customField: "customValue" } */
    fields_responses?: object;
    /** pass ids of preselected hosts */
    hosts_ids?: number[];
}

type EcomSDKCartState = {
    email: string | null;
    token: string | null;
    total: string;
    currency: string;
    acceptsMarketing: boolean;
    billingAddress?: {
        fullName?: string;
        phone?: string;
    };
    items: {
        count: number;
        items: Array<{
            id: string;
            name: string;
            description?: string;
            image?: string;
            quantity: number;
            unitPrice: string;
            sku?: string;
            url?: string;
        }>;
    };
};

type EcomSDKSessionState = {
    storeSettings: {
        defaultCurrency: string;
    };
};

export const ECOMM_EVENTS = [
    'event-ecomm-cartCreated',
    'event-ecomm-cartUpdated',
    'event-ecomm-cartCompleted',
    'event-ecomm-cartCleared',
] as const;

type EcomSDKEventName = (typeof ECOMM_EVENTS)[number];

export interface FormatMoneyPayload {
    amount: number;
    override?: {
        currency?: string | null;
        locale?: string;
    };
}

export interface EcomLocalizationService {
    localize: (
        phrase: string,
        opts?: Record<string, string | number>
    ) => string;
    exists: (key: string) => boolean;
}

export interface EcomSDK {
    ready: Promise<void>;
    openCart: () => void;
    state: {
        cart: EcomSDKCartState;
        session: EcomSDKSessionState;
    };
    events: {
        on: (eventName: EcomSDKEventName, handler: () => void) => () => void;
    };
    addProduct: (params: AddProductPayload) => Promise<void>;
    buyProduct: (params: BuyProductPayload) => Promise<void>;
    buyBooking: (params: BuyBookingPayload) => Promise<void>;
    getOrder: (params: GetOrderPayload) => Promise<OrderResponseDto>;
    requestOrderCancellation: (
        params: RequestOrderCancellationPayload
    ) => Promise<OrderResponseDto>;
    getDigitalResourceDownloadLink: (
        params: GetDigitalResourceDownloadLinkPayload
    ) => Promise<DigitalResourceDownloadLinkResponseDto>;
    formatMoney: (payload: FormatMoneyPayload) => string;
    isFlagEnabled: (payload: IsFlagEnabledPayload) => Promise<boolean>;
    l10n: EcomLocalizationService;
}

export interface IsFlagEnabledPayload {
    name: string;
}

/**
 * Thrown by the ecom SDK when a method fails. `code` is a localization key
 * prefix (e.g. `sdk_errors.order_cancellation_request_outside_window`) that
 * resolves to a `.title` / `.description` pair.
 */
export interface EcomSDKErrorLike {
    code: string;
    message: string;
}

// ── Order types ───────────────────────────────────────────────────────────────

export interface GetOrderPayload {
    order_id: string;
}

export interface RequestOrderCancellationPayload {
    order_id: string;
    /** Optional free-text reason (backend caps at 500 chars). */
    reason?: string;
}

export interface GetDigitalResourceDownloadLinkPayload {
    order_id: string;
    line_item_id: string;
    resource_reference: string;
}

export interface DigitalResourceResponseDto {
    /**
     * Opaque handle (the resource index within the line item) resolved to the
     * S3 key server-side. Pass it back verbatim to the download-link endpoint.
     */
    reference: string;
    title: string;
    /** Bytes. */
    file_size: number;
    media_type: string;
}

export interface DigitalResourceDownloadLinkResponseDto {
    /** Short-lived link that redirects to a presigned S3 URL. */
    url: string;
}

export interface PersonalAddressResponseDto {
    first_name: string | null;
    last_name: string | null;
    full_name: string | null;
    street_number: string | null;
    street_name: string | null;
    address_1: string | null;
    address_2: string | null;
    city: string | null;
    region: string | null;
    sub_locality: string | null;
    country: string | null;
    postal_code: string;
    phone: string;
    name: string | null;
    surname: string | null;
    fullname: string | null;
    street_and_number: string | null;
    line2: string | null;
}

export type ConfirmedPaymentMethodIconName =
    | 'UNKNOWN'
    | 'CARD_UNKNOWN'
    | 'CARD_MASTERCARD'
    | 'CARD_VISA'
    | 'CARD_DISCOVER'
    | 'CARD_AMEX'
    | 'CARD_INTERAC'
    | 'PAYPAL'
    | 'MANUAL'
    | 'CUSTOM'
    | 'NO_PAYMENT'
    | 'BANK_DEBIT'
    | 'BANK_REDIRECT'
    | 'BANK_TRANSFER'
    | 'PAY_LATER'
    | 'VOUCHER'
    | 'REALTIME'
    | 'WALLET'
    | 'POINT_OF_SALE'
    | 'CASH'
    | 'GIFT_CARD'
    | 'CRYPTO';

export type ConfirmedPaymentMethodResponseDto = {
    name: string;
    display_name?: string;
    icon_url?: string;
    icon_name: ConfirmedPaymentMethodIconName;
    details?: string;
    instructions?: string;
};

export type CustomFieldTypeResponseDto =
    | 'TEXT'
    | 'TEXTAREA'
    | 'SELECT'
    | 'RADIO_LIST'
    | 'CHECKBOX'
    | 'DATE';

export type CustomFieldZoneResponseDto =
    | 'CONTACT_INFO'
    | 'SHIPPING_ADDRESS'
    | 'SHIPPING_METHODS'
    | 'BILLING_ADDRESS'
    | 'PAYMENT_METHODS';

export type CustomFieldDateRangePolicyResponseDto =
    | 'ANY'
    | 'PAST_ONLY'
    | 'FUTURE_ONLY';

export interface PickupLocationResponseDto {
    id: string;
    name: string;
    address: {
        address_1: string | null;
        address_2: string | null;
        city: string;
        postal_code: string;
        country: string;
        region: string | null;
        phone: string | null;
    };
    instructions: string | null;
}

interface BaseProductCustomizationResponseDto {
    type: 'TEXT' | 'BOOLEAN' | 'OPTIONS' | 'FILE';
    value?:
        | string
        | boolean
        | string[]
        | ProductCustomizationFileAttachmentResponseDto[];
    id: string;
    price_modifier?: number;
}

export interface ProductCustomizationFileAttachmentResponseDto {
    filename: string;
    mimetype: string;
    size: number;
    url: string;
    key: string;
    handle: string;
    uploaded_at: string;
}

export interface TextProductCustomizationResponseDto
    extends BaseProductCustomizationResponseDto {
    type: 'TEXT';
    value?: string;
    label: string;
}

export interface BooleanProductCustomizationResponseDto
    extends BaseProductCustomizationResponseDto {
    type: 'BOOLEAN';
    value: boolean;
    label: string;
}

export interface OptionsProductCustomizationResponseDto
    extends BaseProductCustomizationResponseDto {
    type: 'OPTIONS';
    value: string[];
    label?: string | null;
    options_price_modifiers?: Array<number | null>;
}

export interface FileProductCustomizationResponseDto
    extends BaseProductCustomizationResponseDto {
    type: 'FILE';
    label: string;
    value: ProductCustomizationFileAttachmentResponseDto[];
}

export type ProductCustomizationResponseDto =
    | TextProductCustomizationResponseDto
    | BooleanProductCustomizationResponseDto
    | OptionsProductCustomizationResponseDto
    | FileProductCustomizationResponseDto;

export interface PurchaseSummaryFeeResponseDto {
    id: string;
    name: string;
    amount: number;
    description?: string;
}

export interface PurchaseSummaryTaxResponseDto {
    id: string;
    name: string;
    percentage: number;
    total_amount: number;
}

export interface PurchaseSummaryResponseDto {
    original_subtotal_amount?: number | null;
    subtotal_amount: number | null;
    discount_amount: number | null;
    shipping_amount: number | null;
    fees: PurchaseSummaryFeeResponseDto[] | null;
    net_total_amount: number | null;
    taxes: PurchaseSummaryTaxResponseDto[] | null;
    total_amount: number;
    total_taxes: number;
}

export interface OrderDiscountResponseDto {
    id: string;
    code: string | null;
    savings: number;
    name: string;
    discount_type: string;
}

export interface OrderTaxResponseDto {
    id: string;
    name: string;
    amount: number;
    rate: number;
}

export interface OrderShippingMethodResponseDto {
    /**
     * The names of every selection joined, for an order shipping in several groups. Read
     * `group_selections` to name one shipment.
     */
    name: string;
    cost: number;
    tax_amount: number;
    /**
     * What shipped each of the order's shipping groups. The API always sends at least one entry —
     * an order placed with a single method for the whole cart gets one covering the default group —
     * so a length above 1 is what says the order shipped in several. Absent on an order the API
     * answered before per-group shipping.
     */
    group_selections?: OrderShippingGroupSelectionResponseDto[];
}

export interface OrderShippingGroupResponseDto {
    /** Null is the default group — the implicit group holding every product assigned to none. */
    id: string | null;
    /** Null for the default group, which the merchant never named. */
    name: string | null;
}

export interface OrderShippingGroupSelectionResponseDto {
    shipping_group_id: string | null;
    courier_id: string | null;
    /**
     * Whatever the provider returned — a merchant-written method name for `BUILT_IN`, the carrier
     * and service for `EASYSHIP`. The API carries no localization id for it, so it is displayed
     * as-is.
     */
    courier_name: string | null;
    shipping_provider: 'UNKNOWN' | 'BUILT_IN' | 'EASYSHIP' | 'EXTERNAL';
    cost: number;
    min_delivery_time: number | null;
    max_delivery_time: number | null;
}

export type OrderPaymentResponseDto = {
    method: string;
    transaction_id: string | null;
    confirmed_method: ConfirmedPaymentMethodResponseDto;
    card_last4_digits: string | null;
    card_type: string | null;
    express_method: string | null;
    icon_url: string | null;
    display_name: string | null;
};

export interface OrderShipmentResponseDto {
    shipment_id: string | null;
    provider_name: 'UNKNOWN' | 'BUILT_IN' | 'EASYSHIP' | 'EXTERNAL';
}

export interface OrderItemDimensionsResponseDto {
    width: number | null;
    height: number | null;
    length: number | null;
}

export interface OrderItemOptionValue {
    name: string;
    value: string;
}

export interface PaymentPlanResponseDto {
    user_defined_id: string;
    name: string;
    interval: number;
    count: number | null;
    frequency: 'WEEKLY' | 'MONTHLY' | 'YEARLY';
    tag_line: string | null;
}

type BookingResponseDtoBase = {
    id: string | null;
    seat_id: string | null;
    slot_start: string;
    slot_duration: number | null;
    time_zone: string;
};

export type BookingResponseDto =
    | (BookingResponseDtoBase & {
          booking_status: 'Pending' | 'Failed' | 'Success';
          creation_status?: never;
      })
    | (BookingResponseDtoBase & {
          booking_status?: never;
          creation_status: 'PENDING' | 'FAILED' | 'SUCCESS';
      });

export interface OrderItemResponseDto {
    id: string;
    type: 'PHYSICAL_PRODUCT' | 'DIGITAL_PRODUCT' | 'MEMBERSHIP' | 'SHIPPING';
    unique_id: string;
    product_id: string | null;
    name: string;
    description: string | null;
    sku: string | null;
    /**
     * The shipping group this item shipped in. Null is the default group, and also covers every
     * item that does not ship at all. The name for this id comes from the order's
     * `shipping_groups`.
     */
    shipping_group_id?: string | null;
    unit_price: number;
    subtotal: number;
    taxable_total: number;
    total: number;
    quantity: number;
    refunded_quantity: number;
    dimensions: OrderItemDimensionsResponseDto | null;
    weight: number | null;
    total_weight: number | null;
    image_url: string | null;
    option_values: OrderItemOptionValue[];
    payment_plan: PaymentPlanResponseDto | null;
    taxes: OrderTaxResponseDto[];
    product_customizations: ProductCustomizationResponseDto[];
    booking: BookingResponseDto | null;
    /** Omitted by the API on items whose `type` is not `DIGITAL_PRODUCT`. */
    digital_resources?: DigitalResourceResponseDto[];
}

export interface OrderCustomFieldResponseDto {
    id: string;
    type: CustomFieldTypeResponseDto;
    zone: CustomFieldZoneResponseDto;
    label: string;
    value: string | null;
    includes_time?: boolean | null;
}

export interface OrderResponseDto {
    is_sandbox_mode: boolean;
    id: string;
    email: string;
    creation_date: string;
    invoice_number: string | null;
    summary: PurchaseSummaryResponseDto;
    billing_address: PersonalAddressResponseDto;
    shipping_address: PersonalAddressResponseDto | null;
    shipping_instructions: string | null;
    currency: string;
    subtotal: number;
    total_discount_amount: number;
    total_tax_amount: number;
    taxable_total: number;
    total: number;
    discounts: OrderDiscountResponseDto[] | null;
    taxes: OrderTaxResponseDto[] | null;
    shipping_method: OrderShippingMethodResponseDto | null;
    /**
     * Every shipping group the order ships in, default group first with a null `id`. This is the
     * one place a group is named — an item carries `shipping_group_id` alone. Empty for an order
     * holding nothing that ships, absent on an order the API answered before per-group shipping.
     */
    shipping_groups?: OrderShippingGroupResponseDto[];
    items: OrderItemResponseDto[];
    order_status: 'OPEN' | 'CLOSED' | 'CANCELLED' | 'CANCELLATION_REQUESTED';
    /**
     * Computed server-side (LaunchDarkly flag AND per-store toggle AND order
     * eligibility — within the cancellation window, not already
     * cancelled/refunded/requested). The storefront uses this as the single
     * source of truth for whether to offer the cancellation option.
     */
    can_request_cancellation: boolean;
    /**
     * False once the order is cancelled or its payment is fully refunded. A
     * partial refund keeps it true, since the refund may target an item
     * unrelated to the digital goods.
     */
    can_download_digital_resources: boolean;
    status:
        | 'IN_PROGRESS'
        | 'PROCESSED'
        | 'SHIPPED'
        | 'DELIVERED'
        | 'PENDING'
        | 'CANCELLED'
        | 'DISPATCHED';
    payment_status:
        | 'PAID'
        | 'AUTHORIZED'
        | 'PENDING'
        | 'UNPAID'
        | 'DISPUTED'
        | 'CHARGED_BACK'
        | 'REFUNDED'
        | 'PARTIALLY_REFUNDED'
        | 'FAILED';
    payment: OrderPaymentResponseDto | null;
    refunded_items_amount: number;
    refunded_taxes_amount: number;
    total_refunded_amount: number;
    shipping_refunded: boolean;
    display_fulfillment_status:
        | 'PROCESSING'
        | 'READY_FOR_PICKUP'
        | 'PICKED_UP'
        | 'UNFULFILLED'
        | 'FULFILLED'
        | 'PARTIALLY_FULFILLED'
        | 'PARTIALLY_SHIPPED'
        | 'SHIPPED'
        | null;
    shipment: OrderShipmentResponseDto | null;
    pickup: PickupLocationResponseDto | null;
    customer_id: string | null;
    language: string | null;
    store_url: string | null;
    has_taxes_included_in_prices: boolean;
    custom_fields: OrderCustomFieldResponseDto[];
}

export type ConfirmedOrderResponseDto = OrderResponseDto & {
    invoice_number: string;
    payment: OrderPaymentResponseDto;
};

export interface OrderFulfillmentResponseDto {
    id: string;
    method: 'NONE' | 'EMAIL_MESSAGE' | 'SHIPMENT' | 'PICKUP';
    status: 'UNKNOWN' | 'IN_PROGRESS' | 'FULFILLED';
    items: {
        id: string;
        fulfilled_quantity: number;
    }[];
    created_on: string;
    last_updated_on: string;
}

export interface OrderUnfulfilledItemResponseDto {
    id: string;
    quantity: number;
    method: 'NONE' | 'EMAIL_MESSAGE' | 'SHIPMENT' | 'PICKUP';
}
